import { request } from "../../platform/apiClient/apiRequest";
import type {
  CafeteriaCouponStatus,
  CafeteriaCouponSummary,
  CafeteriaMenuItem,
  CafeteriaMonthlySummary,
  CafeteriaTransaction,
  CafeteriaWallet,
  MenuItemPatch,
} from "../types/cafeteria.types";

export interface MenuItemInput {
  name: string;
  price: number;
  category: string;
  isActive?: boolean;
}

export interface ChargeInput {
  qrToken: string;
  items: { menuItemId: string; quantity: number }[];
  /** Omit to charge the full order total; set to charge a partial amount instead (must be <= the total). */
  amountEgp?: number;
}

export const cafeteriaClient = {
  getWallet: () => request<CafeteriaWallet>("/cafeteria/wallet"),
  regenerateToken: () => request<CafeteriaWallet>("/cafeteria/wallet/regenerate", { method: "POST" }),
  getWalletTransactions: () => request<CafeteriaTransaction[]>("/cafeteria/wallet/transactions"),
  getMyCoupon: () => request<CafeteriaCouponStatus>("/cafeteria/wallet/coupon"),

  getMenu: () => request<CafeteriaMenuItem[]>("/cafeteria/menu"),
  getAdminMenu: () => request<CafeteriaMenuItem[]>("/cafeteria/menu/admin"),
  createMenuItem: (input: MenuItemInput) =>
    request<CafeteriaMenuItem>("/cafeteria/menu/admin", { method: "POST", body: JSON.stringify(input) }),
  updateMenuItem: (id: string, patch: MenuItemPatch) =>
    request<CafeteriaMenuItem>(`/cafeteria/menu/admin/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteMenuItem: (id: string) => request<void>(`/cafeteria/menu/admin/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // Throws ApiError on 402 (insufficient_funds, with a currentBalance field) and
  // 404 (not_found) — callers branch on err.status, same pattern as everywhere else.
  charge: (input: ChargeInput) => request<CafeteriaWallet>("/cafeteria/charge", { method: "POST", body: JSON.stringify(input) }),

  // Counter-only: check a scanned wallet's coupon status, then redeem it against one item.
  getCouponStatusForToken: (qrToken: string) =>
    request<CafeteriaCouponStatus>(`/cafeteria/coupon/status?qrToken=${encodeURIComponent(qrToken)}`),
  redeemCoupon: (qrToken: string, menuItemId: string) =>
    request<CafeteriaWallet>("/cafeteria/charge/coupon", { method: "POST", body: JSON.stringify({ qrToken, menuItemId }) }),

  getAdminWallets: () => request<CafeteriaWallet[]>("/cafeteria/admin/wallets"),
  topUpWallet: (id: string, amountEgp: number, note?: string) =>
    request<CafeteriaWallet>(`/cafeteria/admin/wallets/${encodeURIComponent(id)}/topup`, {
      method: "POST",
      body: JSON.stringify({ amountEgp, note }),
    }),
  topUpAll: (amountEgp: number, note?: string) =>
    request<{ walletsUpdated: number }>("/cafeteria/admin/topup-all", {
      method: "POST",
      body: JSON.stringify({ amountEgp, note }),
    }),

  getAdminTransactions: () => request<CafeteriaTransaction[]>("/cafeteria/admin/transactions"),
  // Reverses the transaction's effect on the wallet balance as part of deleting it — throws
  // ApiError(409) if that would leave the wallet negative.
  deleteTransaction: (id: string) =>
    request<{ newBalance: number }>(`/cafeteria/admin/transactions/${encodeURIComponent(id)}`, { method: "DELETE" }),
  // Undoes an entire "top up all" event (every row sharing that batchId) at once — throws
  // ApiError(409) if any employee in the batch has already spent part of it.
  deleteTransactionBatch: (batchId: string) =>
    request<{ walletsAffected: number }>(`/cafeteria/admin/transactions/batch/${encodeURIComponent(batchId)}`, { method: "DELETE" }),

  // expiresAt is "YYYY-MM-DD"; omit/undefined means the coupon never expires.
  createCoupon: (description: string, expiresAt?: string) =>
    request<{ couponId: string; walletsIssued: number }>("/cafeteria/admin/coupons", {
      method: "POST",
      body: JSON.stringify({ description, expiresAt }),
    }),
  giveCoupon: (walletId: string, description: string, expiresAt?: string) =>
    request<{ couponId: string }>(`/cafeteria/admin/wallets/${encodeURIComponent(walletId)}/coupon`, {
      method: "POST",
      body: JSON.stringify({ description, expiresAt }),
    }),
  getAdminCoupons: () => request<CafeteriaCouponSummary[]>("/cafeteria/admin/coupons"),
  // Cancels every still-unredeemed slot; already-redeemed ones are untouched. Throws
  // ApiError(409) only once every slot has already been redeemed.
  deleteCoupon: (couponId: string) =>
    request<{ cancelledCount: number }>(`/cafeteria/admin/coupons/${encodeURIComponent(couponId)}`, { method: "DELETE" }),

  // Available to both admin and cafeteria roles.
  getMonthlySummary: () => request<CafeteriaMonthlySummary[]>("/cafeteria/summary"),
};
